/**
 * Аватар пользователя.
 *
 * §31 отдельно требует, чтобы аватар «не съезжал»: в прототипе картинка
 * растягивалась и вылезала из круга. Отсюда фиксированный размер, aspect-square,
 * object-cover и shrink-0 — четыре условия, каждое из которых нужно.
 *
 * Формирование инициалов и ФИО — в personName.ts.
 */

import { resolveApiUrl } from "../api/http";
import { cn } from "./cn";
import { initialsOf } from "./personName";

export type AvatarSize = "xs" | "sm" | "md" | "lg";

interface AvatarPerson {
  firstName: string;
  lastName: string;
  avatarUrl?: string | null;
}

interface AvatarProps {
  person: AvatarPerson;
  size?: AvatarSize;
  className?: string;
}

const SIZES: Record<AvatarSize, string> = {
  xs: "size-5 text-[9px]",
  sm: "size-6 text-[10px]",
  md: "size-8 text-xs",
  lg: "size-12 text-base",
};

export function Avatar({ person, size = "md", className }: AvatarProps) {
  const name = `${person.lastName} ${person.firstName}`.trim();
  const base = cn(
    "inline-flex shrink-0 aspect-square items-center justify-center overflow-hidden rounded-full",
    SIZES[size],
    className,
  );

  if (typeof person.avatarUrl === "string" && person.avatarUrl.length > 0) {
    return (
      <img
        src={resolveApiUrl(person.avatarUrl) ?? undefined}
        alt={name}
        // ФИО при наведении — как у аватара с инициалами.
        title={name}
        className={cn(base, "object-cover")}
        loading="lazy"
      />
    );
  }

  return (
    <span
      className={cn(base, "bg-neutral-soft font-bold text-muted select-none")}
      // Инициалы — не текст для чтения вслух; имя даёт title и aria-label.
      aria-label={name}
      title={name}
    >
      <span aria-hidden>{initialsOf(person)}</span>
    </span>
  );
}

interface AvatarGroupProps {
  people: (AvatarPerson & { id: string })[];
  size?: AvatarSize;
  max?: number;
}

/** Несколько исполнителей на карточке задачи (§11.5). */
export function AvatarGroup({ people, size = "sm", max = 3 }: AvatarGroupProps) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;

  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((person) => (
        <Avatar
          key={person.id}
          person={person}
          size={size}
          className="ring-2 ring-surface"
        />
      ))}
      {rest > 0 && (
        <span
          className={cn(
            "inline-flex shrink-0 aspect-square items-center justify-center rounded-full",
            "bg-neutral-soft font-bold text-muted ring-2 ring-surface",
            SIZES[size],
          )}
          title={people
            .slice(max)
            .map((p) => `${p.lastName} ${p.firstName}`)
            .join(", ")}
        >
          +{rest}
        </span>
      )}
    </div>
  );
}
